import { useCallback, useEffect, useMemo, useState } from "react";
import type { ComponentType, FormEventHandler, ReactNode } from "react";
import { Formik as FormikBase } from "formik";
import { isEmpty, sortBy } from "lodash-es";
import { Icon, Typography } from "@mui/material";
import { RpTab, RpTabs } from "@/components/muiCustom/tabs/JoinCampaignTab";
import JoinCampaignPageSubHeaderBase from "@/features/campaigns/components/joinCampaigns/JoinCampaignPageSubHeader";
import ParticipantPageLayoutBase from "@/components/layouts/page/ParticipantPageLayout";
import RpSnackbarBase from "@/components/alerts/RpSnackbar";
import { joinCampaignHelper } from "@/features/campaigns/shared/helpers/joinCampaignHelper";
import { RpConfirmDialog } from "@/helpers/utils";
import RpConfirm from "@/components/RpConfirm";
import InActivePostCardCampaignLocationsBase from "./InActivePostCardCampaignLocations";
import ActiveLocationsBase from "./activeLocations/ActiveLocations";

import RpAlert from "@/components/alerts/RpAlert";
import LocationsExpandCollapseButtonsBase from "@/features/campaigns/components/LocationsExpandCollapseButtons";
import RpSvgIconBase from "@/components/icons/RpSvgIcon";
import PreviewOnlyDialogBase from "@/features/campaigns/components/joinCampaignCommon/PreviewOnlyDialog";
import useIsParticipant from "@/features/auth/hooks/useIsParticipant";

type ContextId = string | number;

type CampaignLocation = {
  id: ContextId;
  [key: string]: unknown;
};

type PostcardCampaign = {
  budget_type?: string;
  campaign_advanced_setting?: {
    is_required?: boolean;
    [key: string]: unknown;
  };
  campaign_name?: string;
  campaign_type?: string;
  duration?: number | string;
  end_date?: string;
  id?: ContextId;
  local_selection?: boolean;
  locations?: {
    fallOutSide?: CampaignLocation[];
    subscribed?: CampaignLocation[];
    unSubscribed?: CampaignLocation[];
  };
  start_date?: string;
  [key: string]: unknown;
};

type LoggedInUser = {
  email?: string;
  first_name?: string;
  id?: ContextId;
  last_name?: string;
  participantData?: {
    id?: ContextId;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

type JoinPayload = {
  brand_id?: ContextId;
  budget_type?: string;
  campaign_type?: string;
  duration?: number | string;
  email_id?: string;
  end_date?: string;
  fk_campaign_id?: ContextId;
  fk_campaign_name?: string;
  fk_participant_id?: ContextId;
  is_creative_selected?: number;
  is_draft: boolean;
  is_enrolled?: number;
  locationData: string;
  payment_status: number;
  splitBudgetEqually: boolean;
  start_date?: string;
  total_budget: unknown;
  user_id?: ContextId;
  user_name: string;
};

type FormValues = Record<string, any>;

type FormikActions = {
  setSubmitting: (_isSubmitting: boolean) => void;
};

type FormikRenderProps = FormikActions & {
  handleSubmit: FormEventHandler<HTMLFormElement>;
  isSubmitting: boolean;
  setFieldError: (_field: string, _message?: string) => void;
  setFieldValue: (_field: string, _value: unknown) => void;
  values: FormValues;
};

type JoinPostcardCampaignFormProps = {
  campaign?: PostcardCampaign | null;
  getAdvanceSettingData?: unknown;
  initialValues: FormValues;
  isDraft: boolean;
  loggedInUser: LoggedInUser;
  productList?: unknown[];
  selectedBrand?: { id?: ContextId; [key: string]: unknown } | null;
  setIsDraft: (_isDraft: boolean) => void;
  submit: (_payload: JoinPayload) => void;
};

type TabPanelProps = {
  children?: ReactNode;
  index: number;
  value: number;
  [key: string]: unknown;
};

const Formik = FormikBase as ComponentType<any>;
const ActiveLocations = ActiveLocationsBase as ComponentType<any>;
const InActivePostCardCampaignLocations =
  InActivePostCardCampaignLocationsBase as ComponentType<any>;
const JoinCampaignPageSubHeader =
  JoinCampaignPageSubHeaderBase as ComponentType<any>;
const LocationsExpandCollapseButtons =
  LocationsExpandCollapseButtonsBase as ComponentType<any>;
const ParticipantPageLayout = ParticipantPageLayoutBase as ComponentType<any>;
const PreviewOnlyDialog = PreviewOnlyDialogBase as ComponentType<any>;
const confirmDialog = RpConfirmDialog as (..._args: any[]) => void;
const RpSnackbar = RpSnackbarBase as ComponentType<any>;
const RpSvgIcon = RpSvgIconBase as ComponentType<any>;

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`campaigns-tabpanel-${index}`}
      aria-labelledby={`campaigns-tab-${index}`}
      {...other}
      className="rp-campaign-tab"
    >
      {/* {value === index && children} */}
      {children}
    </div>
  );
}

function JoinPostcardCampaignForm({
  campaign,
  initialValues,
  loggedInUser,
  selectedBrand,
  submit,
  setIsDraft,
  productList,
  getAdvanceSettingData,
  isDraft,
}: JoinPostcardCampaignFormProps) {
  const isPreview = !useIsParticipant();
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [formError, setFormError] = useState<{ message?: string }>({});

  const [fillAdvancedSetting, setFillAdvancedSetting] = useState(false);
  const [advancedSettingFormError, setAdvancedSettingFormError] = useState<
    Record<string, unknown>
  >({});

  const [expanded, setExpanded] = useState<ContextId[]>([]);

  useEffect(() => {
    if (!fillAdvancedSetting) {
      setAdvancedSettingFormError({});
    }
  }, [fillAdvancedSetting]);

  const isAdvancedSettingsFormValid = !Object.keys(advancedSettingFormError)
    ?.length;

  const validLocations = useMemo(() => {
    const { subscribed = [] } = campaign?.locations || {};
    return subscribed && subscribed.length ? sortBy(subscribed, "id") : [];
  }, [campaign]);

  const inValidLocationsCount = useMemo(() => {
    const { fallOutSide = [], unSubscribed = [] } = campaign?.locations || {};
    return fallOutSide.length + unSubscribed.length;
  }, [campaign]);

  const allLocationsIds = useMemo(() => {
    const { fallOutSide = [], unSubscribed = [] } = campaign?.locations || {};

    if (selectedTab === 0) {
      return validLocations && validLocations.length
        ? validLocations.map((l) => l.id)
        : [];
    } else if (selectedTab === 1) {
      const fos =
        fallOutSide && fallOutSide.length ? fallOutSide.map((l) => l.id) : [];
      const us =
        unSubscribed && unSubscribed.length
          ? unSubscribed.map((l) => l.id)
          : [];
      return [...fos, ...us];
    }
    return [];
  }, [campaign, selectedTab, validLocations]);

  useEffect(() => {
    if (validLocations.length === 0 && inValidLocationsCount > 0) {
      setSelectedTab(1);
    }
  }, [inValidLocationsCount, validLocations]);

  useEffect(() => {
    if (allLocationsIds.length) {
      if (allLocationsIds.length <= 5) {
        setExpanded([...allLocationsIds]);
      } else {
        setExpanded([allLocationsIds[0]]);
      }
    } else {
      setExpanded([]);
    }
  }, [allLocationsIds]);

  const handleOnChangeAdvancedSettingError = useCallback(
    ({
      locId,
      errors,
    }: {
      errors?: Record<string, unknown>;
      locId: ContextId;
    }) => {
      // const hasKey = Object.keys(advancedSettingFormError)?.includes(locId);
      const hasErr = errors && Object.keys(errors)?.length;
      if (hasErr) {
        setAdvancedSettingFormError((p) => ({ ...p, [locId]: errors }));
      } else {
        setAdvancedSettingFormError((p) => {
          // eslint-disable-next-line no-unused-vars
          const { [locId]: _, ...rest } = p;
          return { ...rest };
        });
      }
    },
    []
  );

  /**
   * @method handleFormSubmit
   * @description handle on submit form
   * @param {*} values
   * @param {*} actions
   * @returns
   */
  const handleFormSubmit = (values: FormValues, actions: FormikActions) => {
    if (isPreview) {
      actions.setSubmitting(false);
      setPreviewDialogOpen(true);
      return;
    }
    setFormError({});

    const { campaign_advanced_setting } = campaign || {};
    const { is_required } = campaign_advanced_setting || {};

    // To check cost factors
    if (!joinCampaignHelper.isPostCardCostFactorExist(campaign)) {
      setFormError({
        message: `Campaign cost factors data is missing! Please connect with admin.`,
      });
      actions.setSubmitting(false);
      return;
    }

    // consider only fully filled locations
    const { locs, locIds } = joinCampaignHelper.getPostCardValidLocations(
      values,
      campaign
    );
    const totalEnrollingLocations = locIds.length;

    const locationsFilledData = locs;

    // check of there is no fully filled location then prevent to submit
    if (locIds.length === 0) {
      setFormError({
        message: `Please make your selections!`,
      });
      actions.setSubmitting(false);
      return;
    }

    // if (campaign_type === "Normal") {
    //   if (
    //     joinCampaignHelper.isNormalCampaignConstraintViolated(
    //       campaign,
    //       locationsFilledData,
    //       setFormError,
    //       locIds
    //     )
    //   ) {
    //     actions.setSubmitting(false);
    //     return;
    //   }
    // } else {
    //   // Check for Evergreen campaign constraints
    //   if (
    //     joinCampaignHelper.isEvergreenCampaignConstraintViolated(
    //       campaign,
    //       locationsFilledData,
    //       setFormError,
    //       locIds
    //     )
    //   ) {
    //     actions.setSubmitting(false);
    //     return;
    //   }
    // }

    const totalActiveLocations = validLocations.length;

    if (totalEnrollingLocations === 0) {
      // if there is no fully filled location prevent to submit
      setFormError({
        message: `Please make your selections!`,
      });
      actions.setSubmitting(false);
      return;
    }

    const locationData =
      joinCampaignHelper.getPostCardCampFormattedLocationData(values, campaign);

    // checking for advanced settings required

    if (!isDraft && !fillAdvancedSetting && is_required) {
      actions.setSubmitting(false);
      setFillAdvancedSetting(true);
      return;
    }

    const saveDraftData: JoinPayload = {
      email_id: loggedInUser.email,
      user_id: loggedInUser.id,
      user_name: `${loggedInUser.first_name} ${loggedInUser.last_name}`,
      fk_participant_id: loggedInUser?.participantData?.id,
      fk_campaign_id: campaign?.id,
      // system_generated_campaign_id: campaign.system_generated_campaign_id,
      fk_campaign_name: campaign?.campaign_name,
      campaign_type: campaign?.campaign_type,
      duration: campaign?.duration,
      start_date: campaign?.start_date,
      end_date: campaign?.end_date,
      locationData: JSON.stringify(locationData),
      payment_status: 0,
      total_budget: joinCampaignHelper.getPostCardLocTotal(locationsFilledData),
      is_draft: true,
      splitBudgetEqually: false,
      budget_type: campaign?.budget_type,
      brand_id: selectedBrand?.id,
      // is_enrolled: 0,
      // status: 1,
    };

    const isConfirming =
      campaign?.budget_type === "Pre-set" && campaign?.local_selection;
    if (isConfirming) {
      saveDraftData.is_draft = false;
      saveDraftData.is_enrolled = 1;
      saveDraftData.is_creative_selected = 1;
    }

    if (totalActiveLocations === totalEnrollingLocations) {
      if (isConfirming) setIsDraft(false);
      submit(saveDraftData);
    } else {
      confirmDialog(
        <RpConfirm
          message={`You're enrolling ${totalEnrollingLocations} of ${totalActiveLocations} locations in this campaign. Are you sure you want to proceed?`}
        />,

        () => {
          if (isConfirming) setIsDraft(false);
          submit(saveDraftData);
        },
        () => {
          setIsDraft(false);
          actions.setSubmitting(false);
          return;
        }
      );
    }
  };

  const renderPage = ({
    values,
    handleSubmit,
    setFieldValue,
    setFieldError,
    isSubmitting,
  }: FormikRenderProps) => {
    return (
      <form onSubmit={handleSubmit}>
        <JoinCampaignPageSubHeader
          onSaveAsDraft={() => setIsDraft(true)}
          campaign_name={campaign?.campaign_name}
          isSubmitting={isSubmitting}
          isAdvancedSettingsFormValid={isAdvancedSettingsFormValid}
          selectedTab={selectedTab}
          allLocationsIds={allLocationsIds}
          isPostcardCampaign={true}
        />
        <div className="join-campaign-form  mx-5">
          <ParticipantPageLayout className="join-campaign-form-page">
            <div className="my-1 pt-5 row">
              <div className="col-xl mb-4 mb-xl-0">
                <Typography component="h2" variant="h6">
                  <Icon
                    component={RpSvgIcon}
                    name="campaign"
                    color="primary"
                    className="mr-2"
                  />
                  {`${campaign?.campaign_name} Campaign Selections`}
                </Typography>
              </div>
              <div className="col-xl col-xl-6 d-flex justify-content-start justify-content-xl-end px-xl-0">
                <div className="row flex-fill align-items-center">
                  <div className="col mb-3 mb-lg-0 d-flex justify-content-center justify-content-sm-start justify-content-xl-end px-xl-0">
                    <RpTabs
                      value={selectedTab}
                      onChange={(event, newValue) => setSelectedTab(newValue)}
                      variant="scrollable"
                      scrollButtons={false}
                    >
                      <RpTab
                        label={`Eligible Locations (${validLocations.length})`}
                        iconPosition="start"
                        icon={
                          <Icon fontSize="inherit" className="mr-1">
                            dashboard
                          </Icon>
                        }
                      />
                      <RpTab
                        label={`Not Eligible Locations (${inValidLocationsCount})`}
                        iconPosition="start"
                        icon={
                          <Icon fontSize="inherit" className="mr-1">
                            dashboard
                          </Icon>
                        }
                      />
                    </RpTabs>
                  </div>
                  <div className="col-lg-3 pl-0 d-flex justify-content-end">
                    <LocationsExpandCollapseButtons
                      setExpanded={setExpanded}
                      expanded={expanded}
                      allLocationsIds={allLocationsIds}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="locations my-3">
              <TabPanel value={selectedTab} index={0}>
                <>
                  {!joinCampaignHelper.isPostCardCostFactorExist(campaign) ? (
                    <RpAlert severity="error" className="mb-2">
                      Campaign cost factors data is missing! Please connect with
                      admin.
                    </RpAlert>
                  ) : null}
                  <ActiveLocations
                    values={values}
                    subscribedLocations={validLocations}
                    setFieldValue={setFieldValue}
                    setFieldError={setFieldError}
                    campaign={campaign}
                    isSubmitting={isSubmitting}
                    onSaveAsDraft={() => setIsDraft(true)}
                    productList={productList}
                    getAdvanceSettingData={getAdvanceSettingData}
                    fillAdvancedSetting={fillAdvancedSetting}
                    setFillAdvancedSetting={setFillAdvancedSetting}
                    isAdvancedSettingsFormValid={isAdvancedSettingsFormValid}
                    handleOnChangeAdvancedSettingError={
                      handleOnChangeAdvancedSettingError
                    }
                    setExpanded={setExpanded}
                    expanded={expanded}
                  />
                </>
              </TabPanel>
              <TabPanel value={selectedTab} index={1}>
                <InActivePostCardCampaignLocations
                  fallOutSideLocations={campaign?.locations?.fallOutSide}
                  unSubscribedLocations={campaign?.locations?.unSubscribed}
                  selectedBrand={selectedBrand}
                  values={values}
                  campaign={campaign}
                  setExpanded={setExpanded}
                  expanded={expanded}
                  setFieldValue={setFieldValue}
                  setFieldError={setFieldError}
                />
              </TabPanel>
            </div>
          </ParticipantPageLayout>
        </div>
      </form>
    );
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleFormSubmit}
      >
        {(props) => renderPage(props)}
      </Formik>

      {!isEmpty(formError) && <RpSnackbar show message={formError.message} />}
      <PreviewOnlyDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
      />
    </>
  );
}

export default JoinPostcardCampaignForm;
